import type { Scene, TextAsset } from '@lumen5/luminary';
import { AspectRatio, TextAlignment, VideoData } from '@lumen5/luminary';

import { createLuminaryOperation } from './luminary-hooks';
import { saveRawLuminary } from './utils';

import { checkpointVideo } from 'js/src/data/video-data';
import uuid from 'js/src/libs/uuid';

export const DISCLAIMER_TEXT_ASSET_NAME = 'disclaimer_text_1';

const WHITE_FILL = 0xffffff;
const SHADOW_COLOR = 0x000000;
const DISCLAIMER_FONT_INDEX = 0;

type DisclaimerLayout = {
    bottomMargin: number;
    maxWidth: number;
    maxHeight: number;
    textSize: number;
};

const RIGHT_BORDER_MARGIN = 20;

// maxWidth, maxHeight, textSize, and bottomMargin mirror the `attribution_1` partial JSON under
// luminary/src/video-data/designs/scenes/partials/attribution/{aspectRatio}.json so the disclaimer
// matches the attribution layer's visual weight. The horizontal inset is uniform instead: the
// disclaimer is right-border-anchored (anchor.x = 1), so it hugs the right edge rather than sitting
// inside the frame like left-anchored attribution.

const NARROW_PORTRAIT_LAYOUT: DisclaimerLayout = {
    bottomMargin: 10,
    maxWidth: 980,
    maxHeight: 800,
    textSize: 21.875,
};

const DISCLAIMER_LAYOUT_BY_ASPECT_RATIO: Record<AspectRatio, DisclaimerLayout> = {
    [AspectRatio.LANDSCAPE]: {
        bottomMargin: 2,
        maxWidth: 1700,
        maxHeight: 800,
        textSize: 25,
    },
    [AspectRatio.SQUARE]: {
        bottomMargin: 10,
        maxWidth: 950,
        maxHeight: 800,
        textSize: 21.875,
    },
    [AspectRatio.VERTICAL]: NARROW_PORTRAIT_LAYOUT,
    [AspectRatio.FOURFIVE]: NARROW_PORTRAIT_LAYOUT,
};

type Resolution = { width: number; height: number };

function applyDisclaimerStyle(asset: TextAsset): void {
    const fontUrl = VideoData.templateFontsByIndex(DISCLAIMER_FONT_INDEX);
    for (const style of [asset.normalStyle, asset.highlightStyle]) {
        style.fontUrl = fontUrl;
        style.fill = WHITE_FILL;
        style.shadow = true;
        style.shadowColor = SHADOW_COLOR;
        style.shadowAlpha = 0.3;
        style.shadowBlur = 10;
        style.shadowAngle = 1;
        style.shadowDistance = 4;
    }
}

function positionAtBottomRight(
    asset: TextAsset,
    layout: DisclaimerLayout,
    resolution: Resolution,
): void {
    asset.transformation.anchor.x = 1;
    asset.transformation.anchor.y = 1;
    asset.transformation.position.x = resolution.width - RIGHT_BORDER_MARGIN;
    asset.transformation.position.y = resolution.height - layout.bottomMargin;
}

function isPositionOffScreen(asset: TextAsset, resolution: Resolution): boolean {
    const { x, y } = asset.transformation.position;
    return x < 0 || x > resolution.width || y < 0 || y > resolution.height;
}

function createDisclaimerAsset(
    scene: Scene,
    text: string,
    layout: DisclaimerLayout,
    resolution: Resolution,
): void {
    const asset = scene.addSticker(VideoData.TEXT_ASSET_TYPE);
    asset.name = DISCLAIMER_TEXT_ASSET_NAME;
    asset.text = text;
    asset.maxWidth = layout.maxWidth;
    asset.maxHeight = layout.maxHeight;
    asset.textSize = layout.textSize;
    asset.textAlign = TextAlignment.RIGHT;
    applyDisclaimerStyle(asset);
    positionAtBottomRight(asset, layout, resolution);
}

export const setDisclaimerTextOnAllScenesOperation = createLuminaryOperation(
    ({ luminary, dispatch }, disclaimerText: string) => {
        const layout = DISCLAIMER_LAYOUT_BY_ASPECT_RATIO[luminary.aspectRatio];
        const resolution = luminary.referenceResolution;

        luminary.batchRun(() => {
            for (const scene of luminary.scenes) {
                const existing = scene.getAssetByName(DISCLAIMER_TEXT_ASSET_NAME) as
                    | TextAsset
                    | undefined;
                if (!existing) {
                    createDisclaimerAsset(scene, disclaimerText, layout, resolution);
                    continue;
                }
                existing.text = disclaimerText;
                if (isPositionOffScreen(existing, resolution)) {
                    positionAtBottomRight(existing, layout, resolution);
                }
            }
        });

        dispatch(
            checkpointVideo({
                video_data: saveRawLuminary(luminary),
                systemTriggered: false,
                parentActionGroup: uuid(),
            }),
        );
    },
);
